import { Component, EventEmitter, forwardRef, Host, Input, OnInit, Optional, Output, SkipSelf } from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCalendar, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { PickerType } from '@busacca/ng-pick-datetime/lib/date-time/date-time.class';
import { UtilitiesService } from 'app/store/services/utilities.servive';

export interface ITextFieldIconProps {
  icon: IconProp;
  position?: 'left' | 'right';
  hoverText?: string;
  updateOnClick?: boolean;
  onClick?: () => void;
}

export enum ETextFieldBackgroundColor {
  WHITE = 'white',
  GREY = 'grey',
}

@Component({
  selector: 'app-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextFieldComponent),
      multi: true,
    },
  ],
})
export class TextFieldComponent implements OnInit, ControlValueAccessor {
  @Input() value: string;
  @Input() disabled: boolean;
  @Input() placeholder: string;
  @Input() label: string;
  @Input() iconProps: ITextFieldIconProps;
  @Input() minUpdateCount: number;
  @Input() type: string;
  @Input() keyChangesTrigger: string;
  @Input() formControlName: string;
  @Input() datePickerType: PickerType = 'both';
  @Input() datePickerStartDate: Date | null = null;
  @Input() fieldBackgroundClass: string | ETextFieldBackgroundColor =
    ETextFieldBackgroundColor.GREY;
  @Input() allowViewPassword: boolean;
  @Input() minutesSteps = 1;
  @Output() changes: EventEmitter<string> = new EventEmitter<string>();
  @Input() canCopy = false;
  control: AbstractControl;
  iconCalendar = faCalendar;
  iconPasswordView = faEye;
  protected readonly faEye = faEye;

  constructor(
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private readonly utilsService: UtilitiesService
  ) {
  }

  propagateChange = (_: any) => {
  };

  propagateTouch = (_: any) => {
  };


  isInputRequired(): boolean {
    if (this.control && this.control.validator) {
      const validator = this.control.validator({} as AbstractControl);
      return validator && validator['required'];
    }
    return false;
  }

  getTestId(predefined?: string) {
    if(predefined) {
      return this.utilsService.getTestIdProp(`${predefined}-input`);
    }

    if(this.label) {
      return this.utilsService.getTestIdProp(`${this.label}-input`);
    }

    if(this.placeholder) {
      return this.utilsService.getTestIdProp(`${this.placeholder}-input`);
    }

    if (typeof this.formControlName === "string") {
      return this.utilsService.getTestIdProp(`${this.formControlName}-input`);
    }

    return this.utilsService.getTestIdProp('input');
  }

  ngOnInit(): void {
    if (this.allowViewPassword && this.type !== 'password') {
      this.allowViewPassword = false;
    }
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName);
      } else {
        console.warn(
          'Missing FormControlName directive from host element of the component',
        );
      }
    } else {
      console.warn('Can\'t find parent FormGroup directive');
    }
    if (!this.type) {
      this.type = 'text';
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Parent FormControl only — do not call propagateChange here (CVA contract; avoids fighting inner ngModel).
   */
  writeValue(obj: any): void {
    this.value = obj;
  }

  onValueChange($event: KeyboardEvent) {
    if (
      this.keyChangesTrigger &&
      $event.key &&
      this.keyChangesTrigger === $event.key
    ) {
      this.changes.next(this.value);
    }
  }

  valueChanges(value) {
    this.value = value;
    this.propagateChange(this.value);
    if(this.type === 'date' && value) {
      this.changes.next(this.value);
      return;
    }
    if (this.minUpdateCount && value && value.length >= this.minUpdateCount) {
      this.changes.next(this.value);
    }
  }

  onIconClick() {
    if (this.iconProps.updateOnClick) {
      this.changes.emit(this.value);
    }

    if (this.iconProps.onClick) {
      this.iconProps.onClick();
    }
  }

  getPlaceholder() {
    if (!this.control) {
      return this.placeholder || '';
    }

    if (this.control.errors?.required) {
      return `${this.placeholder || ''} *`;
    }

    return this.placeholder || '';
  }

  showPassword() {
    this.type = this.type === 'password' ? 'text' : 'password';
    this.iconPasswordView = this.type === 'password' ? faEye : faEyeSlash;
  }
}
